import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useMemo, useState, useTransition } from "react";
import { useForm, UseFormReturn } from "react-hook-form";

import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import KOGAutoCompletePaginationControlled from "@/main/components/shared/inputAutoCompleteWithPagination/autoCompletePaginationControlled";
import KogInputTextControlledNew from "@/main/components/shared/inputText/inputTextControlledNew";
import TooltipComponent from "@/main/components/shared/tooltip/tooltipComponent";

import { supplierApi } from "@/main/api";
import { KeyValue, KeyValueDefault } from "@/main/models/common";
import { SupplierFavoriteRequest } from "@/main/models/request";
import { ReassignForm, ReassignPartData } from "@/main/screens/order/reassignOrder/reassignScreenUtils";
import { getTranslateByKey } from "@/main/services/utils";
import { convertStringForBoolean } from "@/main/services/utilsString";
import { useReasonReducer } from "@/main/store/reason";

import * as S from "./styles";

interface AutofillForm {
	supplier: KeyValue | null;
	partType: KeyValueDefault | null;
	deliveryDeadline: string;
	reason: KeyValueDefault | null;
	reasonDescription: string;
}

const INITIAL_AUTOFILL: AutofillForm = {
	supplier: null,
	partType: null,
	deliveryDeadline: "",
	reason: null,
	reasonDescription: "",
};

type DirectAssignValue = NonNullable<ReassignForm["parts"][number]["directAssign"]>;

const EMPTY_DIRECT_ASSIGN: DirectAssignValue = {
	supplier: null,
	partType: null,
	deliveryDeadline: null,
	price: null,
};

interface IAutofillReassign {
	partsData: ReassignPartData[];
	partTypeOptionsByQuote: Record<string, KeyValueDefault[]>;
	guidCompanyBuyer?: string;
	mainForm: UseFormReturn<ReassignForm>;
}

const ID_BASE = "id-reassignOrder-autofill";

export default function AutofillReassign({
	partsData,
	partTypeOptionsByQuote,
	guidCompanyBuyer,
	mainForm,
}: Readonly<IAutofillReassign>) {
	const { control, setValue, watch } = useForm<AutofillForm>({
		mode: "onChange",
		defaultValues: INITIAL_AUTOFILL,
	});

	const [isApplying, startTransition] = useTransition();

	const { reason: reasonReducer } = useReasonReducer();
	const [fieldApplying, setFieldApplying] = useState<string | null>(null);
	const reasonValue = watch("reason");
	const showDescription = reasonValue !== null && convertStringForBoolean(reasonValue?.default);

	const allPartTypeOptions = useMemo(() => {
		const map = new Map<string, KeyValueDefault>();
		Object.values(partTypeOptionsByQuote).forEach((options) => {
			options.forEach((opt) => {
				const key = String(opt.default);
				if (!map.has(key)) map.set(key, opt);
			});
		});
		return Array.from(map.values());
	}, [partTypeOptionsByQuote]);

	function applyDirectAssignField(
		field: "supplier" | "partType" | "deliveryDeadline",
		value: KeyValue | KeyValueDefault | string | null,
		isEligible?: (part: ReassignPartData) => boolean,
	) {
		partsData.forEach((part, partIndex) => {
			const current = mainForm.getValues(`parts.${partIndex}.directAssign`);
			const eligible = !isEligible || isEligible(part);

			if (current && !eligible) return;

			if (!current) {
				mainForm.setValue(`parts.${partIndex}.selectedGuidQuoteSupplierItem`, null);
			}

			const base = current ?? EMPTY_DIRECT_ASSIGN;
			mainForm.setValue(`parts.${partIndex}.directAssign`, {
				...base,
				...(eligible ? { [field]: value } : {}),
			} as DirectAssignValue);

			if (eligible) {
				mainForm.clearErrors(`parts.${partIndex}.directAssign.${field}`);
			}
		});
	}

	function applyValue(value: string, field: "deliveryDeadline" | "reasonDescription") {
		if (field === "reasonDescription") {
			partsData.forEach((_, partIndex) => {
				mainForm.setValue(`parts.${partIndex}.${field}`, value);
				mainForm.clearErrors(`parts.${partIndex}.${field}`);
			});
			return;
		}

		applyDirectAssignField("deliveryDeadline", value);
	}

	function applyOption(option: KeyValue | KeyValueDefault | null, field: "supplier" | "partType" | "reason") {
		setFieldApplying(field);
		if (field === "reason") {
			partsData.forEach((_, partIndex) => {
				mainForm.setValue(`parts.${partIndex}.reason`, option as KeyValueDefault);
				mainForm.clearErrors(`parts.${partIndex}.reason`);
			});
			return;
		}

		if (field === "partType") {
			const optionDefault = option !== null ? (option as KeyValueDefault).default : null;
			applyDirectAssignField("partType", option, (part) => {
				if (optionDefault === null) return true;
				const optionsForThisQuote = partTypeOptionsByQuote[part.quoteGuid ?? ""] ?? [];
				return optionsForThisQuote.some((opt) => String(opt.default) === String(optionDefault));
			});
			return;
		}

		applyDirectAssignField("supplier", option);
	}

	return (
		<S.Container>
			<S.Header>
				<S.Title>{getTranslateByKey("information.autofill")}</S.Title>
				<TooltipComponent title={getTranslateByKey("information.tooltipAutofill")}>
					<S.IconWrapper>
						<InfoOutlinedIcon fontSize="small" />
					</S.IconWrapper>
				</TooltipComponent>
			</S.Header>

			<S.FieldsRow>
				<S.Field $minWidth="200px">
					<KOGAutoCompletePaginationControlled
						name="supplier"
						label={getTranslateByKey("information.supplier")}
						loadingMessage={`${getTranslateByKey("information.searchingForSuppliers")}...`}
						idScreen={ID_BASE}
						control={control}
						onChangeCustom={(option) => startTransition(() => applyOption(option, "supplier"))}
						getData={async (requestProp) => {
							const request: SupplierFavoriteRequest = {
								...requestProp,
								guidCompanyBuyer: String(guidCompanyBuyer),
							};
							return supplierApi.getFavoriteSuppliersByCompanyBuyer(request);
						}}
						disabled={isApplying && fieldApplying !== "supplier"}
					/>
				</S.Field>

				<S.Field $minWidth="180px">
					<KogAutoCompleteControlled
						id={`${ID_BASE}-partType`}
						nameCustom="partType"
						label={getTranslateByKey("information.partTypes")}
						control={control}
						setValueCustom={setValue}
						options={allPartTypeOptions}
						isLoading={fieldApplying === "partType" && isApplying}
						disabled={isApplying && fieldApplying !== "partType"}
						onChangeCustom={(option) => startTransition(() => applyOption(option, "partType"))}
					/>
				</S.Field>

				<S.Field $minWidth="135px">
					<KogInputTextControlledNew
						id={`${ID_BASE}-deliveryDeadline`}
						label={getTranslateByKey("information.deliveryDeadline")}
						name="deliveryDeadline"
						control={control}
						onChangeCustom={(value) => startTransition(() => applyValue(value, "deliveryDeadline"))}
						hasOnlyNumber
						hasOnlyNumberInteger
						disabled={isApplying && fieldApplying !== "deliveryDeadline"}
					/>
				</S.Field>

				<S.Field $minWidth="200px">
					<KogAutoCompleteControlled
						id={`${ID_BASE}-reason`}
						nameCustom="reason"
						label={getTranslateByKey("information.reason")}
						control={control}
						setValueCustom={setValue}
						options={reasonReducer.data}
						isLoading={reasonReducer.isLoading || (fieldApplying === "reason" && isApplying)}
						disabled={isApplying && fieldApplying !== "reason"}
						onChangeCustom={(option) => startTransition(() => applyOption(option, "reason"))}
					/>
				</S.Field>

				{showDescription && (
					<S.Field $minWidth="200px">
						<KogInputTextControlledNew
							id={`${ID_BASE}-reasonDescription`}
							label={getTranslateByKey("information.description")}
							name="reasonDescription"
							control={control}
							onChangeCustom={(value) => startTransition(() => applyValue(value, "reasonDescription"))}
						/>
					</S.Field>
				)}
			</S.FieldsRow>
		</S.Container>
	);
}
